import React from "react";

/**
 * Compact label + input wrapper used across the alert-rule form fields.
 * Keeps every field-row visually consistent and lets the form body stay
 * declarative — one component per row.
 */
function Field({ label, colSpan = 1, hint, children }) {
  return (
    <div className={colSpan === 2 ? "col-span-2" : ""}>
      <label className="label-mono">{label}</label>
      {children}
      {hint && <div className="mt-1 text-[10px] text-zinc-500">{hint}</div>}
    </div>
  );
}

/** Number input bound to a single form field (with min/max/step passthrough). */
function NumberField({ form, setForm, name, label, testId, min, max, step }) {
  return (
    <Field label={label}>
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        required={min !== undefined}
        data-testid={testId}
        className="w-full border border-black px-3 py-2 mt-1 font-mono"
        value={form[name]}
        onChange={(e) => setForm({ ...form, [name]: Number(e.target.value) })}
      />
    </Field>
  );
}

/** Multi-select of destination rows (used when scope=pick). */
function DestinationPicker({ form, setForm, rows }) {
  return (
    <Field label="Destinations" colSpan={2}>
      <select
        multiple
        className="w-full border border-black p-2 mt-1 font-mono text-xs h-24"
        value={form.destination_ids}
        onChange={(e) => setForm({
          ...form,
          destination_ids: Array.from(e.target.selectedOptions).map((o) => o.value),
        })}
      >
        {(rows || []).map((r) => (
          <option key={r.destination_id} value={r.destination_id}>
            {r.name} · {r.type}
          </option>
        ))}
      </select>
    </Field>
  );
}

/**
 * The alert-rule creation form. Deliberately controlled — parent owns the
 * `form` state so we can prefill / reset from outside.
 */
export default function AlertForm({ form, setForm, rows, onSubmit }) {
  return (
    <form onSubmit={onSubmit} className="brut-card p-4 mb-3 grid grid-cols-2 gap-3" data-testid="alert-form">
      <Field label="Rule name*">
        <input
          required data-testid="alert-name"
          className="w-full border border-black px-3 py-2 mt-1"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </Field>
      <Field label="Scope">
        <select
          data-testid="alert-scope"
          className="w-full border border-black px-2 py-1.5 mt-1"
          value={form.scope}
          onChange={(e) => setForm({ ...form, scope: e.target.value })}
        >
          <option value="all">All active destinations</option>
          <option value="pick">Pick destinations…</option>
        </select>
      </Field>

      {form.scope === "pick" && <DestinationPicker form={form} setForm={setForm} rows={rows} />}

      <NumberField form={form} setForm={setForm} name="threshold_pct"
        label="Threshold %* (fire when <)" testId="alert-threshold" min={0} max={100} step="0.1" />
      <NumberField form={form} setForm={setForm} name="window_minutes"
        label="Window (min, 5–1440)" testId="alert-window" min={5} max={1440} />
      <NumberField form={form} setForm={setForm} name="min_samples"
        label="Min samples" min={1} />
      <NumberField form={form} setForm={setForm} name="cooldown_minutes"
        label="Cooldown (min)" min={5} />

      <Field label="Notify email">
        <input
          type="email" data-testid="alert-email"
          className="w-full border border-black px-3 py-2 mt-1"
          placeholder="ops@example.com"
          value={form.notify_email}
          onChange={(e) => setForm({ ...form, notify_email: e.target.value })}
        />
      </Field>
      <Field label="Slack webhook URL">
        <input
          type="url" data-testid="alert-slack"
          className="w-full border border-black px-3 py-2 mt-1 font-mono text-xs"
          placeholder="https://hooks.slack.com/services/..."
          value={form.notify_slack_webhook}
          onChange={(e) => setForm({ ...form, notify_slack_webhook: e.target.value })}
        />
      </Field>

      <div className="col-span-2 flex justify-end">
        <button
          type="submit" data-testid="alert-submit"
          className="bg-black text-white border border-black px-4 py-2 uppercase text-xs tracking-widest font-bold"
        >
          Create rule
        </button>
      </div>
    </form>
  );
}
